import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getList,
  getStatusOfProcessing,
  extractData,
  getExtractedData,
  updateList,
  analyseData,
  getAnalysisData,
} from '../../../services/processing';

export const PROCESSING_KEYS = {
  list: (id) => ['processing', 'list', id],
  status: (id) => ['processing', 'status', id],
  extractions: (id) => ['processing', 'extractions', id],
  analysis: (id) => ['processing', 'analysis', id],
};

const ALREADY_EXTRACTED_MSG = 'Data Already Extracted!';
const ALREADY_ANALYSED_MSG = 'Data Already Analysed!';

// ─── Fetch the document list by ID ──────────────────────────────────────────

export const useGetList = (id) => {
  return useQuery({
    queryKey: PROCESSING_KEYS.list(id),
    queryFn: () => getList(id),
    enabled: !!id,
  });
};

// ─── Check extraction / analysis status ─────────────────────────────────────

export const useGetStatus = (id, { enabled = true } = {}) => {
  return useQuery({
    queryKey: PROCESSING_KEYS.status(id),
    queryFn: () => getStatusOfProcessing(id),
    enabled: !!id && enabled,
  });
};

// ─── Extractions ─────────────────────────────────────────────────────────────

export const useExtractions = (id, extractionStatus) => {
  const queryClient = useQueryClient();

  // When status flips to PENDING, wipe stale cached extraction data immediately
  useEffect(() => {
    if (extractionStatus === 'PENDING') {
      queryClient.removeQueries({ queryKey: PROCESSING_KEYS.extractions(id) });
    }
  }, [extractionStatus, id, queryClient]);

  // const extractMutation = useMutation({
  //   mutationFn: () => extractData(id),
  //   onSuccess: (data) => {
  //     if (Array.isArray(data?.extractions)) {
  //       // Fresh data — write directly into cache
  //       queryClient.setQueryData(PROCESSING_KEYS.extractions(id), data);
  //     } else {
  //       // "Data Already Extracted!" came back as a resolved shape — force the getter
  //       queryClient.fetchQuery({
  //         queryKey: PROCESSING_KEYS.extractions(id),
  //         queryFn: () => getExtractedData(id),
  //       });
  //     }
  //   },
  //   onError: (err) => {
  //     const msg = err?.message ?? err?.detail ?? '';
  //     if (msg.includes(ALREADY_EXTRACTED_MSG)) {
  //       // "Data Already Extracted!" came back as a thrown error — force the getter
  //       queryClient.fetchQuery({
  //         queryKey: PROCESSING_KEYS.extractions(id),
  //         queryFn: () => getExtractedData(id),
  //       });
  //     }
  //   },
  // });
  const extractMutation = useMutation({
    mutationFn: () => extractData(id),
    onSuccess: (data) => {
      if (Array.isArray(data?.extractions)) {
        queryClient.setQueryData(PROCESSING_KEYS.extractions(id), data);
      } else {
        queryClient
          .fetchQuery({
            queryKey: PROCESSING_KEYS.extractions(id),
            queryFn: () => getExtractedData(id),
          })
          .catch(() => {}); // already reflected in extractionsQuery.isError — just prevent unhandled rejection
      }
    },
    onError: (err) => {
      const msg = err?.message ?? err?.detail ?? '';
      if (msg.includes(ALREADY_EXTRACTED_MSG)) {
        queryClient
          .fetchQuery({
            queryKey: PROCESSING_KEYS.extractions(id),
            queryFn: () => getExtractedData(id),
          })
          .catch(() => {});
      }
    },
  });

  const extractionsQuery = useQuery({
    queryKey: PROCESSING_KEYS.extractions(id),
    queryFn: () => getExtractedData(id),
    enabled: extractionStatus === 'COMPLETE' && !!id,
  });

  return { extractMutation, extractionsQuery };
};

// ─── Update the list (save results) ─────────────────────────────────────────

export const useUpdateList = ({ onSuccess, onError } = {}) => {
  return useMutation({
    mutationFn: ({ listId, pdfArray }) => updateList({ listId, pdfArray }),
    onSuccess: (data) => onSuccess?.(data),
    onError: (err) => onError?.(err),
  });
};

// ─── Analysis ────────────────────────────────────────────────────────────────

export const useAnalysis = (id, analysisStatus) => {
  const queryClient = useQueryClient();

  // When status flips to PENDING, wipe stale cached analysis data immediately
  useEffect(() => {
    if (analysisStatus === 'PENDING') {
      queryClient.removeQueries({ queryKey: PROCESSING_KEYS.analysis(id) });
    }
  }, [analysisStatus, id, queryClient]);

  const analysisMutation = useMutation({
    mutationFn: () => analyseData(id),
    onSuccess: (data) => {
      if (Array.isArray(data?.analysis)) {
        queryClient.setQueryData(PROCESSING_KEYS.analysis(id), data);
      } else {
        // "Data Already Analysed!" resolved shape — force the getter
        queryClient.fetchQuery({
          queryKey: PROCESSING_KEYS.analysis(id),
          queryFn: () => getAnalysisData(id),
        });
      }
    },
    onError: (err) => {
      const msg = err?.message ?? err?.detail ?? '';
      if (msg.includes(ALREADY_ANALYSED_MSG)) {
        // "Data Already Analysed!" thrown error — force the getter
        queryClient.fetchQuery({
          queryKey: PROCESSING_KEYS.analysis(id),
          queryFn: () => getAnalysisData(id),
        });
      }
    },
  });

  const analysisQuery = useQuery({
    queryKey: PROCESSING_KEYS.analysis(id),
    queryFn: () => getAnalysisData(id),
    enabled: analysisStatus === 'COMPLETE' && !!id,
  });

  return { analysisMutation, analysisQuery };
};
